/**
 * Whether the activity panel is open, remembered per recording.
 *
 * Per *file*, not per session: a recording somebody is analysing reopens the way they left it,
 * while the next recording they open starts collapsed — which is the default, because most
 * transcripts are read rather than analysed.
 *
 * `localStorage` is the right store and the only one: this is a per-viewer convenience with no
 * meaning to anybody else, it must survive a reload, and nothing else needs to read it. Every
 * access is wrapped, because the accessor itself throws in a browser set to block site data and a
 * dialog must not fail to open over a remembered checkbox.
 */

import { useCallback, useState } from "react";

const STORAGE_PREFIX = "df.transcript.timeline.";

function storageKey(artifactId: string): string {
  return `${STORAGE_PREFIX}${artifactId}`;
}

function readRemembered(artifactId: string | null): boolean {
  if (artifactId === null) return false;
  try {
    return window.localStorage.getItem(storageKey(artifactId)) === "1";
  } catch {
    return false;
  }
}

export type UseRememberedTimelineExpansionResult = {
  isExpanded: boolean;
  setExpanded: (expanded: boolean) => void;
};

export function useRememberedTimelineExpansion(
  artifactId: string | null
): UseRememberedTimelineExpansionResult {
  // The remembered value is read when the recording changes rather than in an effect, so the
  // panel's first paint is already the state the person left it in — an effect would open it a
  // frame later and push the transcript down under their eyes.
  const [state, setState] = useState(() => ({
    artifactId,
    isExpanded: readRemembered(artifactId),
  }));
  if (state.artifactId !== artifactId) {
    setState({ artifactId, isExpanded: readRemembered(artifactId) });
  }

  const setExpanded = useCallback(
    (expanded: boolean) => {
      setState({ artifactId, isExpanded: expanded });
      if (artifactId === null) return;
      try {
        if (expanded) window.localStorage.setItem(storageKey(artifactId), "1");
        else window.localStorage.removeItem(storageKey(artifactId));
      } catch {
        // A viewer whose browser refuses storage still gets the panel; it simply does not
        // outlive the dialog.
      }
    },
    [artifactId]
  );

  return { isExpanded: state.artifactId === artifactId && state.isExpanded, setExpanded };
}
